import React, { useEffect, useState } from "react";
import { Chart } from "react-chartjs-2";
import { fetchStockData } from "../../ApisService/apiService";
import style from '../../../css/dashboard/fundamental/Chart.module.css';
import { motion } from "framer-motion";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarController, // <-- ADD THIS
    LineController, // <-- And this for 'line' type (optional, but safe)
    BarElement,
    Title,
    Tooltip,
    Legend
  } from "chart.js";
  
  // ✅ Register Chart.js components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    BarController, // ✅ Register bar controller
    LineController, // ✅ Register line controller
    Title,
    Tooltip,
    Legend
  );

const StockChart = ({ selectedCompany }) => {
    const [stockData, setStockData] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [selectedMonths, setSelectedMonths] = useState(12);
    const [yAxisLimits, setYAxisLimits] = useState({ min: null, max: null });

    useEffect(() => {
        if (stockData.length === 0) return;

        const closePrices = stockData.map((item) => parseFloat(item.Close) || 0);

        // ✅ Calculate min and max price with padding
        const minPrice = Math.min(...closePrices) * 0.95; // 5% padding below
        const maxPrice = Math.max(...closePrices) * 1.05; // 5% padding above

        setYAxisLimits({ min: minPrice, max: maxPrice });

    }, [stockData]);


    useEffect(() => {
        if (!selectedCompany?.scripcode) {
            setStockData([]);
            setChartData(null);
            return;
        }
        const getData = async () => {
            const data = await fetchStockData(selectedCompany.scripcode, selectedMonths);
            setStockData(data);
        };

        getData();
    }, [selectedCompany?.scripcode, selectedMonths]);

    useEffect(() => {
        if (stockData.length === 0) return;

        // const labels = stockData.map((item) => item.Date.split(" ")[0]);
        const labels = stockData.map((item, index) => {
            const date = new Date(item.Date.trim());
            const formattedDate = `${date.getDate()}-${date.toLocaleString('en-US', { month: 'short' })}`;
        
            if (selectedMonths === 0.25) return formattedDate;
            if (selectedMonths === 1) return formattedDate;
            if (selectedMonths === 3 && index % 2 === 0) return formattedDate;
            if (selectedMonths === 6 && index % 5 === 0) return formattedDate;
            if (selectedMonths === 12 && index % 6 === 0) return formattedDate;
        
            return "";
        });
        

        const closePrices = stockData.map((item) => parseFloat(item.Close) || 0);
        const volume = stockData.map((item) => parseFloat(item.Volume) || 0);

        // ✅ Moving Average Calculation
        const calculateMovingAverage = (prices, period) => {
            return prices.map((_, i, arr) =>
                i < period - 1
                    ? null
                    : arr.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0) / period
            );
        };

        const ma50 = calculateMovingAverage(closePrices, 50);
        const ma200 = calculateMovingAverage(closePrices, 200);


        const createGradient = (ctx, color) => {
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, `${color}88`);
            gradient.addColorStop(1, `${color}00`);
            return gradient;
        };

        // ✅ Combined Chart Data
        setChartData({
            labels,
            datasets: [
                {
                    type: "bar",
                    label: "Volume",
                    data: volume,
                    backgroundColor: goldColor, // Light bars for dark mode
                    borderWidth: 0,
                    barThickness: 4, // Slim bars
                    maxBarThickness: 5, // Controls max width
                    minBarLength: 2,
                    borderRadius: 2, // Slightly rounded
                    yAxisID: "y-axis-left",
                },
                {
                    type: "line",
                    label: "Price",
                    data: closePrices,
                    borderColor: goldColor,
                    backgroundColor: (context) => createGradient(context.chart.ctx, "#0096FF"),
                    fill: true,
                    tension: 0.3,
                    borderWidth: 2,
                    pointRadius: 0,
                    yAxisID: "y-axis-right",
                },
                {
                    type: "line",
                    label: "50-Day MA",
                    data: ma50,
                    borderColor: goldColor, // Gold color
                    borderDash: [5, 5],
                    borderWidth: 2,
                    fill: false,
                    tension: 0.3,
                    pointRadius: 0,
                    yAxisID: "y-axis-right",
                },
                {
                    type: "line",
                    label: "200-Day MA",
                    data: ma200,
                    borderColor: goldColor, // Bright gold
                    borderDash: [5, 5],
                    borderWidth: 2,
                    fill: false,
                    tension: 0.3,
                    pointRadius: 0,
                    yAxisID: "y-axis-right",
                },
            ],
        });

    }, [stockData]);
    const goldColor = getComputedStyle(document.documentElement).getPropertyValue('--gold').trim();

    return (
        <div className={style.chartContainer}>
            {!selectedCompany?.scripcode ? (
                <h3 className={style.warningText}>Please select a stock</h3>
            ) : (
                <>
                    {/* Month Selector  */}
                    <div className={style.chartmonthcontainer}>


                        <div className={style.buttonContainer}>
                            {[
                                { label: "1W", value: 0.25 }, 
                                { label: "1M", value: 1 },
                                { label: "3M", value: 3 },
                                { label: "6M", value: 6 },
                                { label: "12M", value: 12 },
                            ].map(({ label, value }) => (
                                <motion.button
                                    key={value}
                                    onClick={() => setSelectedMonths(value)}
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                    className={`${style.monthButton} ${selectedMonths === value ? style.active : ""}`}
                                >
                                    {label}
                                </motion.button>
                            ))}
                        </div>

                    </div>


                    {/* ✅ Modern Styled Chart */}
                    <div className={style.chartBox}>
                        {chartData ? (
                            <Chart
                                type="bar"
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        "y-axis-left": {
                                            type: "linear",
                                            position: "left",
                                            title: { display: true, text: "Volume", color: goldColor },
                                            grid: { drawOnChartArea: false, drawTicks: false, drawBorder: false },
                                            ticks: {
                                                color: goldColor,
                                                callback: function (value) {
                                                    return value >= 1000 ? `${value / 1000}K` : value; // ✅ Convert to 'K' format
                                                },
                                            },
                                        },
                                        "y-axis-right": {
                                            type: "linear",
                                            position: "right",
                                            title: { display: true, text: "Stock Price", color: goldColor },
                                            grid: { drawOnChartArea: true, drawTicks: false, drawBorder: false, display: true, color: goldColor },
                                            ticks: {
                                                color: goldColor,
                                                callback: function (value) {
                                                    return Math.round(value); // ✅ No decimal values
                                                },
                                            },
                                            min: yAxisLimits.min,
                                            max: yAxisLimits.max,
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: {
                                                color: goldColor,
                                                autoSkip: true,
                                                maxRotation: 0,
                                                minRotation: 0,
                                            },
                                        },
                                    },
                                    plugins: {
                                        legend: {
                                            display: true,
                                            position: "top",
                                            align: "center",
                                            labels: {
                                                color: "gray",
                                                font: {
                                                    size: 10,
                                                    family: "Arial",

                                                },
                                                boxWidth: 10,
                                                boxHeight: 10,
                                                usePointStyle: true,
                                                pointStyle: "rectRounded",
                                                padding: 10,
                                            }
                                        },
                                        tooltip: {
                                            enabled: true,
                                            backgroundColor: "rgba(0,0,0,0.8)"
                                        }
                                    },

                                }}
                            />
                        ) : (
                            <p className={style.loadingText}>Loading data...</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default StockChart;
